import { Logger } from '@frontend/logger';
import { IUser } from '@frontend/user/types';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useIdentification, useScanners } from '../../../hooks';
import { useScreen } from '../useScreen';

interface ViewProps {
    userOverwrite?: IUser;
    resolving: boolean;
    handleSubmit: () => void;
    submitDisabled: boolean;
    error: string | null;
    resetError: () => void;
}

const SENSOR_TIMEOUT = 10000; // 10 seconds in milliseconds

const useIdentifyUser = (): ViewProps => {
    const intl = useIntl();
    const { onSubmit, updateWizardData, wizardData } = useScreen();
    const { userOverwrite } = wizardData;
    const [resolving, setResolving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const sensorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useScanners({
        id: 'IDENTIFY_USER',
        onDeviceSensorReceived: () => {
            setResolving(true);
            sensorTimeoutRef.current = setTimeout(() => {
                setResolving(false);
                setError(intl.formatMessage({ id: 'HOME.ERRORS.CODE_NOT_FOUND', defaultMessage: 'No matching identification found. Please try again.' }));
                sensorTimeoutRef.current = null;
            }, SENSOR_TIMEOUT);
        }
    });

    const clearTimeoutRef = () => {
        if (sensorTimeoutRef.current) {
            clearTimeout(sensorTimeoutRef.current);
            sensorTimeoutRef.current = null;
        }
    };

    const onFail = () => {
        clearTimeoutRef();
        setResolving(false);
        setError(intl.formatMessage({ id: 'HOME.ERRORS.CODE_NOT_FOUND', defaultMessage: 'No matching identification found. Please try again.' }));
    };

    const onUserIdentified = async (user: IUser) => {
        clearTimeoutRef();
        setError(null);
        try {
            updateWizardData({ userOverwrite: user });
            setResolving(false);
        } catch (e) {
            Logger.error('Failed to identify user in identify-user screen', e);
            onFail();
        }
    };

    useIdentification({
        id: 'IDENTIFY_USER',
        onNoMatchingIdentificationFound: onFail,
        onUnlinkedIdentificationFound: () => {
            clearTimeoutRef();
            setResolving(false);
            setError(
                intl.formatMessage({
                    id: 'kiosk.wizard.identify-user.unlinked-identification',
                    description: 'Error shown when a scanned badge is recognized but not linked to a user',
                    defaultMessage: 'This badge was recognized but is not linked to a user yet. Please try a different badge or contact support.'
                })
            );
        },
        onUserIdentified
    });

    const submitDisabled = resolving;

    const handleSubmit = () => {
        if (!submitDisabled) {
            onSubmit();
        }
    };

    useEffect(() => {
        return () => {
            clearTimeoutRef();
        };
    }, []);

    return {
        userOverwrite,
        resolving,
        handleSubmit,
        submitDisabled,
        error,
        resetError: () => setError(null)
    };
};

export default useIdentifyUser;
